import React, { useState, useEffect } from 'react';
import { fieldServiceAPI, hardwareAPI } from '../services/api';

const FieldTechnicianPortal = ({ technicianId, technicianName }) => {
  const [activeTab, setActiveTab] = useState('myTickets');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateNotes, setUpdateNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'myTickets' || activeTab === 'allTickets') {
        const filters = activeTab === 'myTickets' 
          ? { assigned_to: technicianId, status: 'open' }
          : { status: 'open' };
        const response = await fieldServiceAPI.getTickets(filters);
        setTickets(response.data);
      } else if (activeTab === 'maintenance') {
        // Load maintenance tasks assigned to this technician
        const response = await fieldServiceAPI.getMaintenanceSchedule();
        setMaintenanceSchedule(response.data?.filter(m => m.assigned_to === technicianId) || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    if (ticket.robot_id) {
      try {
        const diagResponse = await hardwareAPI.getRobotDiagnostics(ticket.robot_id);
        setDiagnostics(diagResponse.data);
      } catch (error) {
        console.error('Failed to load diagnostics:', error);
      }
    }
  };

  const handleUpdateTicket = async (ticketId, status) => {
    try {
      await fieldServiceAPI.updateTicket(ticketId, {
        status,
        notes: updateNotes,
        updated_by: technicianId
      });
      setUpdateNotes('');
      loadData();
      setSelectedTicket(null);
      alert('Ticket updated successfully');
    } catch (error) {
      alert('Failed to update ticket: ' + error.message);
    }
  };

  const handleCompleteMaintenanceTask = async (taskId) => {
    const notes = prompt('Enter completion notes:');
    if (!notes) return;

    try {
      await fieldServiceAPI.completeMaintenanceTask(taskId, notes);
      alert('Maintenance task marked as complete');
      loadData();
    } catch (error) {
      alert('Failed to complete task: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-900 border-red-500 text-red-400';
      case 'high':
        return 'bg-orange-900 border-orange-500 text-orange-400';
      case 'medium':
        return 'bg-yellow-900 border-yellow-500 text-yellow-400';
      case 'low':
        return 'bg-green-900 border-green-500 text-green-400';
      default:
        return 'bg-gray-700 border-gray-500 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Field Service Portal</h1>
        <p className="text-gray-400">Welcome back, {technicianName}</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-700">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('myTickets')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'myTickets'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            My Tickets ({tickets.filter(t => t.assigned_to === technicianId).length})
          </button>
          <button
            onClick={() => setActiveTab('allTickets')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'allTickets'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            All Open Tickets
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'maintenance'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Maintenance Schedule
          </button>
        </div>
      </div>

      {/* Tickets View */}
      {(activeTab === 'myTickets' || activeTab === 'allTickets') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-white mb-4">
                {activeTab === 'myTickets' ? 'My Tickets' : 'All Open Tickets'}
              </h2>
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedTicket?.ticket_id === ticket.ticket_id
                        ? 'bg-gray-700 border-blue-500'
                        : 'bg-gray-750 border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-white font-medium">#{ticket.ticket_id}</span>
                      <span className={`text-xs px-2 py-1 rounded border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    <p className="text-white text-sm mb-2">{ticket.title}</p>
                    <div className="text-xs text-gray-400">
                      <p>Robot: {ticket.robot_id}</p>
                      <p>Hospital: {ticket.hospital_name}</p>
                      <p>Created: {new Date(ticket.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                {tickets.length === 0 && (
                  <p className="text-gray-400 text-center py-8">No tickets found</p>
                )}
              </div>
            </div>
          </div>

          {/* Ticket Details */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <div className="space-y-6">
                {/* Ticket Info */}
                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">
                        Ticket #{selectedTicket.ticket_id}
                      </h2>
                      <span className={`inline-block px-3 py-1 rounded border ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority.toUpperCase()} PRIORITY
                      </span>
                    </div>
                    {selectedTicket.assigned_to === technicianId && (
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.ticket_id, 'resolved')}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                      >
                        Mark Resolved
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-gray-400 text-sm">Category</p>
                      <p className="text-white font-medium">{selectedTicket.category}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Status</p>
                      <p className="text-white font-medium">{selectedTicket.status}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Robot ID</p>
                      <p className="text-white font-medium">{selectedTicket.robot_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Hospital</p>
                      <p className="text-white font-medium">{selectedTicket.hospital_name}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                    <p className="text-gray-300">{selectedTicket.description}</p>
                  </div>

                  {selectedTicket.assigned_to === technicianId && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Add Notes</h3>
                      <textarea
                        value={updateNotes}
                        onChange={(e) => setUpdateNotes(e.target.value)}
                        placeholder="Enter work notes or updates..."
                        className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:outline-none focus:border-blue-500"
                        rows="4"
                      />
                      <button
                        onClick={() => handleUpdateTicket(selectedTicket.ticket_id, 'in_progress')}
                        className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        Save Notes
                      </button>
                    </div>
                  )}
                </div>

                {/* Diagnostics Data */}
                {diagnostics && (
                  <div className="bg-gray-800 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Robot Diagnostics</h3>
                    <div className="space-y-3">
                      {Object.entries(diagnostics.components || {}).map(([component, data]) => (
                        <div key={component} className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{component}</span>
                            <span className={`text-sm ${
                              data.status === 'operational' ? 'text-green-400' :
                              data.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {data.status}
                            </span>
                          </div>
                          {data.error_message && (
                            <p className="text-sm text-red-400 mt-1">{data.error_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-6 flex items-center justify-center h-96">
                <p className="text-gray-400 text-center">Select a ticket to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Maintenance Schedule View */}
      {activeTab === 'maintenance' && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Scheduled Maintenance Tasks</h2>
          <div className="space-y-4">
            {maintenanceSchedule.map((task, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-lg mb-2">{task.task_name}</h3>
                    <p className="text-gray-300 text-sm mb-2">{task.description}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Robot ID</p>
                        <p className="text-white">{task.robot_id}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Hospital</p>
                        <p className="text-white">{task.hospital_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Due Date</p>
                        <p className="text-white">{new Date(task.due_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Status</p>
                        <p className="text-white">{task.status}</p>
                      </div>
                    </div>
                  </div>
                  {task.status === 'scheduled' && (
                    <button
                      onClick={() => handleCompleteMaintenanceTask(task.task_id)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors ml-4"
                    >
                      Complete
                    </button>
                  )}
                </div>
              </div>
            ))}
            {maintenanceSchedule.length === 0 && (
              <p className="text-gray-400 text-center py-8">No scheduled maintenance tasks</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldTechnicianPortal;
